import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hostingApi, type CreateSiteRequest, type EnvVar } from "./hostingApi";
import { useAccessToken } from "@/auth/useAccessToken";

const keys = {
    sites: (token: string | undefined) => ["hosting", "sites", token] as const,
    site: (id: string, token: string | undefined) => ["hosting", "site", id, token] as const,
    logs: (id: string, token: string | undefined) => ["hosting", "logs", id, token] as const,
    env: (id: string, token: string | undefined) => ["hosting", "env", id, token] as const
};

/** Polls while any site is still creating/building so status transitions show up without a
 * manual refresh — a create/redeploy typically resolves within tens of seconds. */
function pollIntervalFor(sites: { status: string }[] | undefined) {
    return sites?.some((s) => s.status === "creating" || s.status === "building") ? 3000 : false;
}

export function useSites() {
    const token = useAccessToken();
    return useQuery({
        queryKey: keys.sites(token),
        queryFn: () => hostingApi.listSites(token),
        enabled: Boolean(token),
        refetchInterval: (query) => pollIntervalFor(query.state.data)
    });
}

export function useSite(id: string | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: id ? keys.site(id, token) : ["hosting", "site", "none"],
        queryFn: () => (id ? hostingApi.getSite(id, token) : Promise.reject(new Error("no id"))),
        enabled: Boolean(id) && Boolean(token),
        refetchInterval: (query) => {
            const s = query.state.data?.status;
            return s === "creating" || s === "building" ? 3000 : false;
        }
    });
}

export function useSiteLogs(id: string | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: id ? keys.logs(id, token) : ["hosting", "logs", "none"],
        queryFn: () => (id ? hostingApi.getLogs(id, token) : Promise.resolve("")),
        enabled: Boolean(id) && Boolean(token),
        refetchInterval: 5000
    });
}

export function useEnvVars(id: string | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: id ? keys.env(id, token) : ["hosting", "env", "none"],
        queryFn: () => (id ? hostingApi.listEnvVars(id, token) : Promise.resolve([])),
        enabled: Boolean(id) && Boolean(token)
    });
}

const invalidateSites = (qc: ReturnType<typeof useQueryClient>) => {
    void qc.invalidateQueries({ queryKey: ["hosting", "sites"] });
    void qc.invalidateQueries({ queryKey: ["hosting", "site"] });
};

export function useCreateSite() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (req: CreateSiteRequest) => hostingApi.createSite(req, token),
        onSuccess: () => invalidateSites(qc)
    });
}

export function useRedeploySite() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => hostingApi.redeploySite(id, token),
        onSuccess: () => invalidateSites(qc)
    });
}

export function useRestartSite() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => hostingApi.restartSite(id, token),
        onSuccess: () => invalidateSites(qc)
    });
}

export function useDeleteSite() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => hostingApi.deleteSite(id, token),
        onSuccess: () => invalidateSites(qc)
    });
}

export function useReplaceEnvVars(id: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (envVars: EnvVar[]) => hostingApi.replaceEnvVars(id, envVars, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: keys.env(id, token) })
    });
}
